import { DatePipe } from '@angular/common'
import { HttpParams } from '@angular/common/http'
import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, output, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

import {
    BaseComponent,
    CREWS_TIMELINE_DAYS,
    CrewMemberReassignment,
    ForemanCrewsTimeline,
    IsoDate,
    ModalBackDrop,
    ModalSize,
    TimeOffEntry,
    addDaysIso,
    isoDateStringForApi,
    todayIso,
} from '../../../shared'
import {
    ForemanScheduleCrewReassignmentModalComponent,
} from '../foreman-schedule-crew-reassignment-modal.component'

export interface TimelineBarVm {
    leftPct: number
    widthPct: number
    status: 'Approved' | 'Pending' | 'Reassignment'
    leaveType: string
    comment: string | null
    startDate: IsoDate
    endDate: IsoDate
    tooltip: string
}

export interface CrewMemberRowVm {
    hrRef: number
    name: string
    jobTitle: string
    isForeman: boolean
    bars: TimelineBarVm[]
}

export interface BorrowedMemberRowVm {
    reassignmentId: string
    hrRef: number
    name: string
    jobTitle: string
    fromForemanName: string
    bars: TimelineBarVm[]
}

export interface ForemanGroupVm {
    hrRef: number
    name: string
    jobTitle: string
    expanded: boolean
    members: CrewMemberRowVm[]
    borrowedMembers: BorrowedMemberRowVm[]
}

export interface TimelineTickVm {
    label: string
    leftPct: number
}

export interface HolidayMarkerVm {
    date: IsoDate
    name: string
    shortLabel: string
    leftPct: number
    widthPct: number
    isPartialDay: boolean
    tooltip: string
}

@Component({
    selector: 'app-foreman-schedule-crews-tab',
    templateUrl: './foreman-schedule-crews-tab.component.html',
    styleUrls: ['./foreman-schedule-crews-tab.component.css'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DatePipe],
})
export class ForemanScheduleCrewsTabComponent extends BaseComponent {
    private readonly destroyRef = inject(DestroyRef)

    /** When true, loads timeline data on first activation. */
    readonly isActive = input(false)
    readonly canEdit = input(true)
    readonly reassignments = input<CrewMemberReassignment[]>([])
    /** Bumped by the shell when the foreman roster changes; triggers a timeline reload. */
    readonly foremanRevision = input(0)
    readonly reassignmentsChange = output<CrewMemberReassignment[]>()

    readonly isLoading = signal(false)
    readonly loadError = signal<string | null>(null)
    readonly timeline = signal<ForemanCrewsTimeline | null>(null)
    readonly expandedForemanHrRefs = signal<Set<number>>(new Set())

    private hasLoaded = false
    private foremanRevisionSnapshot = 0

    readonly rangeStart = computed(() =>
        isoDateStringForApi(this.timeline()?.dateRangeStart as string | Date | null | undefined) || todayIso(),
    )
    readonly rangeEnd = computed(() =>
        isoDateStringForApi(this.timeline()?.dateRangeEnd as string | Date | null | undefined) ||
        addDaysIso(todayIso(), CREWS_TIMELINE_DAYS),
    )

    readonly foremanGroups = computed(() => {
        const data = this.timeline()
        if (!data) return [] as ForemanGroupVm[]
        const expanded = this.expandedForemanHrRefs()
        const reassignments = this.reassignments()
        const start = isoDateStringForApi(data.dateRangeStart as string | Date | null | undefined)
        const end = isoDateStringForApi(data.dateRangeEnd as string | Date | null | undefined)
        return data.foremen.map(f => ({
            hrRef: f.hrRef,
            name: f.name,
            jobTitle: f.jobTitle,
            expanded: expanded.has(f.hrRef),
            members: f.members.map(m => ({
                hrRef: m.hrRef,
                name: m.name,
                jobTitle: m.jobTitle,
                isForeman: m.isForeman,
                bars: [
                    ...m.timeOff.map(entry => this.toTimelineBar(entry, start, end)),
                    ...this.outboundReassignmentBars(m.hrRef, f.hrRef, reassignments, start, end),
                ],
            })),
            borrowedMembers: this.borrowedMembersForForeman(f.hrRef, data, reassignments, start, end),
        }))
    })

    readonly timelineTicks = computed((): TimelineTickVm[] => {
        const start = this.rangeStart()
        const end = this.rangeEnd()
        const totalDays = this.daysBetweenInclusive(start, end)
        if (totalDays <= 0) return []

        const ticks: TimelineTickVm[] = []
        const stepDays = 14
        for (let offset = 0; offset <= totalDays; offset += stepDays) {
            const iso = addDaysIso(start, offset)
            ticks.push({
                label: this.formatShortDate(iso),
                leftPct: (offset / totalDays) * 100,
            })
        }
        if (ticks.length === 0 || ticks[ticks.length - 1].leftPct < 100) {
            ticks.push({ label: this.formatShortDate(end), leftPct: 100 })
        }
        return ticks
    })

    readonly holidayMarkers = computed((): HolidayMarkerVm[] => {
        const data = this.timeline()
        if (!data?.holidays?.length) return []
        const start = this.rangeStart()
        const end = this.rangeEnd()
        return data.holidays.map(h => {
            const date = isoDateStringForApi(h.date as string | Date | null | undefined)
            const { leftPct, widthPct } = this.toSingleDaySpan(date, start, end)
            const partial = h.isPartialDay ? ' (partial day)' : ''
            const tooltip = `${h.name}${partial}\n${this.formatShortDate(date)}`
            return {
                date,
                name: h.name,
                shortLabel: this.holidayShortLabel(h.name),
                leftPct,
                widthPct,
                isPartialDay: h.isPartialDay,
                tooltip,
            }
        })
    })

    constructor() {
        super()
        effect(() => {
            if (this.isActive() && !this.hasLoaded) {
                this.loadTimeline()
            }
        })

        effect(() => {
            const revision = this.foremanRevision()
            if (revision === this.foremanRevisionSnapshot) return
            this.foremanRevisionSnapshot = revision
            if (!this.hasLoaded) return
            if (this.isActive()) {
                this.loadTimeline()
            } else {
                // Reload the next time the tab is activated.
                this.hasLoaded = false
            }
        })
    }

    refresh() {
        this.hasLoaded = false
        this.reassignmentsChange.emit([])
        this.loadTimeline()
    }

    removeReassignment(id: string) {
        const next = this.reassignments().filter(r => r.id !== id)
        this.reassignmentsChange.emit(next)
        this.persistReassignments(next)
    }

    toggleForeman(hrRef: number) {
        this.expandedForemanHrRefs.update(set => {
            const next = new Set(set)
            if (next.has(hrRef)) next.delete(hrRef)
            else next.add(hrRef)
            return next
        })
    }

    async openReassignmentModal(group: ForemanGroupVm, member: CrewMemberRowVm) {
        const timeline = this.timeline()
        if (!timeline || member.isForeman) return

        const ref = this.services.modals.open(
            ForemanScheduleCrewReassignmentModalComponent,
            ModalSize.medium,
            ModalBackDrop.darkAndInactive,
        )
        const modal = ref.componentInstance as ForemanScheduleCrewReassignmentModalComponent
        modal.initFromContext({
            memberHrRef: member.hrRef,
            memberName: member.name,
            fromForemanHrRef: group.hrRef,
            fromForemanName: group.name,
            foremen: timeline.foremen,
            existing: this.reassignments(),
            defaultStartDate: todayIso(),
        })

        ref.result.then(
            (reassignment: CrewMemberReassignment) => {
                const next = [...this.reassignments(), reassignment]
                this.reassignmentsChange.emit(next)
                this.persistReassignments(next)
            },
            () => undefined,
        )
    }

    /** Auto-saves the entire shared reassignment list (replace-all on the server). */
    private persistReassignments(list: CrewMemberReassignment[]) {
        if (!this.canEdit()) return

        const reassignments = list.map(r => ({
            memberHrRef: r.memberHrRef,
            memberName: r.memberName,
            fromForemanHrRef: r.fromForemanHrRef,
            fromForemanName: r.fromForemanName,
            toForemanHrRef: r.toForemanHrRef,
            toForemanName: r.toForemanName,
            startDate: r.startDate,
            endDate: r.endDate,
        }))

        this.services.data
            .put('api/ForemanSchedule/SaveReassignments', { reassignments })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => undefined,
                error: () => undefined,
            })
    }

    private loadTimeline() {
        this.isLoading.set(true)
        this.loadError.set(null)
        const start = todayIso()
        const end = addDaysIso(start, CREWS_TIMELINE_DAYS)
        const params = new HttpParams().set('startDate', start).set('endDate', end)
        this.services.data
            .get<ForemanCrewsTimeline>('api/ForemanSchedule/GetCrewsTimeline', params)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: data => {
                    this.hasLoaded = true
                    this.isLoading.set(false)
                    this.timeline.set(this.normalizeTimeline(data))
                    this.expandedForemanHrRefs.set(new Set((data.foremen ?? []).map(f => f.hrRef)))
                },
                error: () => {
                    this.isLoading.set(false)
                    this.loadError.set('Failed to load crews and time off.')
                },
            })
    }

    private borrowedMembersForForeman(
        foremanHrRef: number,
        data: ForemanCrewsTimeline,
        reassignments: CrewMemberReassignment[],
        rangeStart: IsoDate,
        rangeEnd: IsoDate,
    ): BorrowedMemberRowVm[] {
        const memberByHrRef = new Map<number, { name: string; jobTitle: string }>()
        for (const f of data.foremen) {
            for (const m of f.members) {
                if (!memberByHrRef.has(m.hrRef)) {
                    memberByHrRef.set(m.hrRef, { name: m.name, jobTitle: m.jobTitle })
                }
            }
        }

        return reassignments
            .filter(r => r.toForemanHrRef === foremanHrRef)
            .map(r => {
                const member = memberByHrRef.get(r.memberHrRef)
                return {
                    reassignmentId: r.id,
                    hrRef: r.memberHrRef,
                    name: member?.name ?? r.memberName,
                    jobTitle: member?.jobTitle ?? '',
                    fromForemanName: r.fromForemanName,
                    bars: [this.toReassignmentBar(`From ${r.fromForemanName}`, r.startDate, r.endDate, rangeStart, rangeEnd)],
                }
            })
    }

    private outboundReassignmentBars(
        memberHrRef: number,
        fromForemanHrRef: number,
        reassignments: CrewMemberReassignment[],
        rangeStart: IsoDate,
        rangeEnd: IsoDate,
    ): TimelineBarVm[] {
        return reassignments
            .filter(r => r.memberHrRef === memberHrRef && r.fromForemanHrRef === fromForemanHrRef)
            .map(r => this.toReassignmentBar(`With ${r.toForemanName}`, r.startDate, r.endDate, rangeStart, rangeEnd))
    }

    private toReassignmentBar(
        label: string,
        startDate: IsoDate,
        endDate: IsoDate,
        rangeStart: IsoDate,
        rangeEnd: IsoDate,
    ): TimelineBarVm {
        const clampedStart = startDate < rangeStart ? rangeStart : startDate
        const clampedEnd = endDate > rangeEnd ? rangeEnd : endDate
        const totalDays = this.daysBetweenInclusive(rangeStart, rangeEnd)
        const startOffset = this.daysBetweenInclusive(rangeStart, clampedStart) - 1
        const spanDays = this.daysBetweenInclusive(clampedStart, clampedEnd)
        const { leftPct, widthPct } = this.toSpanPosition(startOffset, spanDays, totalDays)
        const tooltip = `${label}\n${this.formatShortDate(startDate)} – ${this.formatShortDate(endDate)}`
        return {
            leftPct,
            widthPct,
            status: 'Reassignment',
            leaveType: label,
            comment: null,
            startDate,
            endDate,
            tooltip,
        }
    }

    private normalizeTimeline(data: ForemanCrewsTimeline): ForemanCrewsTimeline {
        return {
            ...data,
            dateRangeStart: isoDateStringForApi(data.dateRangeStart as string | Date | null | undefined),
            dateRangeEnd: isoDateStringForApi(data.dateRangeEnd as string | Date | null | undefined),
            foremen: (data.foremen ?? []).map(f => ({
                ...f,
                members: (f.members ?? []).map(m => ({
                    ...m,
                    timeOff: (m.timeOff ?? []).map(t => ({
                        ...t,
                        startDate: isoDateStringForApi(t.startDate as string | Date | null | undefined),
                        endDate: isoDateStringForApi(t.endDate as string | Date | null | undefined),
                    })),
                })),
            })),
            holidays: (data.holidays ?? []).map(h => ({
                ...h,
                date: isoDateStringForApi(h.date as string | Date | null | undefined),
            })),
        }
    }

    private toTimelineBar(entry: TimeOffEntry, rangeStart: IsoDate, rangeEnd: IsoDate): TimelineBarVm {
        const startDate = isoDateStringForApi(entry.startDate as string | Date | null | undefined)
        const endDate = isoDateStringForApi(entry.endDate as string | Date | null | undefined)
        const totalDays = this.daysBetweenInclusive(rangeStart, rangeEnd)
        const clampedStart = startDate < rangeStart ? rangeStart : startDate
        const clampedEnd = endDate > rangeEnd ? rangeEnd : endDate
        const startOffset = this.daysBetweenInclusive(rangeStart, clampedStart) - 1
        const spanDays = this.daysBetweenInclusive(clampedStart, clampedEnd)
        const { leftPct, widthPct } = this.toSpanPosition(startOffset, spanDays, totalDays)
        const typeLabel = entry.leaveType?.trim() || 'Time off'
        const tooltip = `${typeLabel} (${entry.status})\n${this.formatShortDate(startDate)} – ${this.formatShortDate(endDate)}${
            entry.comment ? `\n${entry.comment}` : ''
        }`
        return {
            leftPct,
            widthPct,
            status: entry.status,
            leaveType: typeLabel,
            comment: entry.comment,
            startDate,
            endDate,
            tooltip,
        }
    }

    private toSingleDaySpan(date: IsoDate, rangeStart: IsoDate, rangeEnd: IsoDate): { leftPct: number; widthPct: number } {
        const totalDays = this.daysBetweenInclusive(rangeStart, rangeEnd)
        const startOffset = this.daysBetweenInclusive(rangeStart, date) - 1
        return this.toSpanPosition(startOffset, 1, totalDays)
    }

    private toSpanPosition(startOffset: number, spanDays: number, totalDays: number): { leftPct: number; widthPct: number } {
        const leftPct = totalDays <= 1 ? 0 : (startOffset / (totalDays - 1)) * 100
        const widthPct = totalDays <= 1 ? 100 : Math.max((spanDays / totalDays) * 100, 0.8)
        return { leftPct, widthPct }
    }

    private holidayShortLabel(name: string): string {
        const trimmed = (name ?? '').trim()
        if (!trimmed) return 'Hol'
        const words = trimmed.split(/\s+/).filter(Boolean)
        if (words.length === 1) return trimmed.length <= 8 ? trimmed : trimmed.slice(0, 7) + '…'
        return words.map(w => w[0]?.toUpperCase() ?? '').join('')
    }

    private daysBetweenInclusive(start: string | Date, end: string | Date): number {
        const startIso = isoDateStringForApi(start as string | Date | null | undefined)
        const endIso = isoDateStringForApi(end as string | Date | null | undefined)
        const [sy, sm, sd] = startIso.split('-').map(n => parseInt(n, 10))
        const [ey, em, ed] = endIso.split('-').map(n => parseInt(n, 10))
        const s = Date.UTC(sy, sm - 1, sd)
        const e = Date.UTC(ey, em - 1, ed)
        return Math.max(1, Math.round((e - s) / 86_400_000) + 1)
    }

    private formatShortDate(iso: string | Date): string {
        const startIso = isoDateStringForApi(iso as string | Date | null | undefined)
        const [y, m, d] = startIso.split('-').map(n => parseInt(n, 10))
        const dt = new Date(Date.UTC(y, m - 1, d))
        return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })
    }
}
